import React from 'react';
import debounce from 'lodash/debounce';
import {
  FormInput,
  FormInputIcon,
  IconEmail,
  IconSearch,
} from '@spotify-internal/encore-web';
import { useRouter } from 'next/router';
import { Box, Link } from '../ui';
import styled from 'styled-components';
import { SearchHit, SearchQuery, SearchResponse, searchLoader } from '.';
import {
  body1FontSize,
  body1LineHeight,
  cta4FontSize,
  cta4LineHeight,
  spacer12,
  spacer16,
  spacer4,
  spacer8,
} from '@spotify-internal/encore-foundation';

type SearchBarProps = {
  query?: string; // set initial query
  contentTypes?: string[]; // restrict content types, can include boost e.g. faq^2
  placeholder?: string; // placeholder text
  search?: boolean; // allow search on enter - redirects to search page
  searchUrl?: string; // url to redirect to if search enabled
  suggest?: boolean; // show suggestions
  suggestCount?: number; // number of suggestions to show
  suggestInitial?: boolean; // show suggestions on no query
  suggester?: (query: SearchQuery) => Promise<SearchResponse>;
  showFooter?: boolean; // show contact us footer
  trackCategory?: string; // category for event tracking
};

const SearchBarWrapper = styled(Box).attrs({
  noPadding: true,
})`
  margin-bottom: ${spacer16};
  background: transparent;
`;

const Suggestions = styled.ul<{ width: number }>`
  position: absolute;
  background: ${({ theme }) => theme.searchBar.background};
  padding-left: 0;
  padding-right: 0;
  border: 1px solid ${({ theme }) => theme.searchBar.border};
  border-top: 0;
  margin-left: ${spacer4};
  z-index: 1000;
  width: ${({ width }) => `${width - 8}px`};
  max-height: 50vh;
  overflow: auto;
`;

const Suggestion = styled.li<{ selected?: boolean }>`
  margin: 0;
  padding-top: ${spacer8};
  padding-bottom: ${spacer8};
  padding-left: ${spacer16};
  padding-right: ${spacer16};
  font-size: ${body1FontSize};
  line-height: ${body1LineHeight};
  background: ${({ theme, selected = false }) =>
    selected ? theme.searchBar.suggestionSelectedBackground : 'transparent'};
  &:hover {
    background: ${({ theme }) => theme.searchBar.suggestionSelectedBackground};
  }
`;

const SuggestionHeader = styled.li`
  text-transform: uppercase;
  font-size: ${cta4FontSize};
  line-height: ${cta4LineHeight};
  padding-top: ${spacer12};
  padding-left: ${spacer12};
  padding-right: ${spacer12};
  color: ${({ theme }) => theme.searchBar.suggestionHeader};
`;

const SuggestionFooter = styled(Suggestion)`
  svg {
    margin-right: ${spacer8};
    vertical-align: -3px;
    color: ${({ theme }) => theme.searchBar.icon};
  }
`;

export const SearchBar: React.FC<SearchBarProps> = ({
  query = '',
  contentTypes,
  placeholder = '',
  search = false,
  suggest = true,
  suggestCount = 10,
  suggestInitial = false,
  searchUrl,
  trackCategory = 'Search Bar',
  showFooter = true,
  suggester = searchLoader.load.bind(searchLoader),
}) => {
  const router = useRouter();
  const input: any = React.useRef(null);

  const debug =
    Boolean(router?.query?.debug_search) ||
    process.env.NODE_ENV === 'development';

  const [suggestions, setSuggestions] = React.useState<SearchHit[]>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(suggestInitial);
  const [selectedSuggestion, setSelectedSuggestion] = React.useState(-1);

  // make refs array for suggestions
  const sugRefs = React.useRef<HTMLLIElement[]>([]);
  React.useEffect(() => {
    sugRefs.current = sugRefs.current.slice(
      0,
      suggestions.length + (showFooter ? 1 : 0),
    );
  }, [suggestions, sugRefs, showFooter]);

  const hideSuggestions = React.useCallback(() => {
    suggest && setShowSuggestions(false);
  }, [suggest, setShowSuggestions]);

  React.useEffect(() => {
    window.addEventListener('click', hideSuggestions);
    window.addEventListener('resize', hideSuggestions);
    const onKeyUp = (e: KeyboardEvent) =>
      suggest && e.key === 'Escape' && setShowSuggestions(false);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('click', hideSuggestions);
      window.removeEventListener('resize', hideSuggestions);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [hideSuggestions, suggest]);

  const doSearch = React.useCallback(() => {
    let url = `${searchUrl || router.pathname}?query=${input.current.value}`;
    if (contentTypes) {
      url += `&contentTypes=${contentTypes.join(',')}`;
    }
    router.push(url);
  }, [input, router, contentTypes, searchUrl]);

  const updateSuggestions = debounce(async () => {
    const searchQuery: SearchQuery = {
      query: input.current.value,
      limit: suggestCount,
      suggest: true,
      debug,
      contentTypes,
    };

    const resp = await suggester(searchQuery);

    if (debug) {
      // eslint-disable-next-line no-console
      console.log('Search Response', resp);
    }

    setShowSuggestions(true);
    if (resp) {
      setSuggestions(resp.hits.filter(Boolean));
    } else {
      setSuggestions([]);
    }
  }, 200);

  const resetSearch = React.useCallback(() => {
    if (suggest) {
      setShowSuggestions(suggestInitial);
      setSelectedSuggestion(-1);
      input.current.value = '';
    }
  }, [
    suggestInitial,
    input,
    setSelectedSuggestion,
    setShowSuggestions,
    suggest,
  ]);

  // scroll current suggestion into view
  React.useEffect(() => {
    const el = sugRefs.current[selectedSuggestion];
    if (el) el.scrollIntoView(false);
  }, [selectedSuggestion]);

  return (
    <SearchBarWrapper>
      <FormInputIcon icon={IconSearch}>
        <FormInput
          type="text"
          placeholder={placeholder}
          ref={input}
          defaultValue={query || ''}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setShowSuggestions(input.current?.value?.trim() || suggestInitial);
          }}
          onKeyUp={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              // navigate to selected suggestion
              if (
                suggest &&
                showSuggestions &&
                suggestions &&
                selectedSuggestion >= 0
              ) {
                const url = suggestions[selectedSuggestion]?.url;
                if (url) {
                  router.push(url);
                  resetSearch();
                }
              } else if (search) {
                doSearch();
              }
            } else if (suggest) {
              if (e.key === 'Escape') {
                setShowSuggestions(false);
              } else if (showSuggestions && e.key === 'ArrowDown') {
                e.stopPropagation();
                setSelectedSuggestion(i =>
                  i >= suggestions.length ? 0 : i + 1,
                );
              } else if (showSuggestions && e.key === 'ArrowUp') {
                setSelectedSuggestion(i =>
                  i <= 0 ? suggestions.length : i - 1,
                );
              } else if (e.key !== 'Escape') {
                updateSuggestions();
              }
            }
          }}
        />
      </FormInputIcon>

      {suggest &&
        showSuggestions &&
        (input.current?.value?.trim() || suggestInitial) && (
          <Suggestions
            width={input.current?.clientWidth || 300}
            data-testid="search-suggestions"
          >
            <SuggestionHeader>Results ({suggestions.length})</SuggestionHeader>

            {suggestions.map((sug, idx) => (
              <Suggestion
                key={sug.id}
                selected={selectedSuggestion === idx}
                ref={el => {
                  sugRefs.current[idx] = el!;
                }}
              >
                <Link track={['Search Suggestion', sug.url]} href={sug.url}>
                  {sug.title}
                </Link>
                {debug && sug.score && ` [${sug.score.toFixed(2)}]`}
              </Suggestion>
            ))}

            {showFooter && (
              <>
                <SuggestionHeader>
                  Can't find what you&apos;re looking for?
                </SuggestionHeader>
                <SuggestionFooter
                  selected={selectedSuggestion === suggestions.length}
                  ref={el => {
                    sugRefs.current[suggestions.length] = el!;
                  }}
                >
                  <Link href="/contact" track={[trackCategory, 'Contact Us']}>
                    <IconEmail iconSize={16} aria-label="Contact us" />
                    Contact us
                  </Link>
                </SuggestionFooter>
              </>
            )}
          </Suggestions>
        )}
    </SearchBarWrapper>
  );
};
